import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type MeterReading = {
  unit_id: number
  unit_number: string
  room_type: string
  // The room type the unit belongs to. The bulk readings page groups on this
  // id (two room types can share a name; the name is display only).
  room_type_id: number
  // The room type's free-text floor ("4", "ชั้น3"), null when it has none.
  room_type_floor: string | null
  meter_id: number
  serial_number: string | null
  meter_type: string
  latest_reading: {
    id: number
    read_at: string
    previous_value: number
    current_value: number
    usage: number
  } | null
  // current_value of the most recent reading saved BEFORE the requested
  // month — used to pre-fill the Previous column when the selected month
  // has no reading yet (forward continuity).
  previous_month_value: number | null
  // previous_value of the first reading saved AFTER the requested month —
  // used to pre-fill the Current column when the selected month has no
  // reading yet (backward continuity).
  next_month_previous_value: number | null
}

export type PropertyReadingsData = {
  water: MeterReading[]
  electricity: MeterReading[]
}

interface BulkReadingsResponse {
  data: PropertyReadingsData
}

interface BulkReadingInput {
  meter_id: number
  current_value: number
  previous_value?: number
  // MON-14 (T149-028): resubmits a row whose current_value is below the
  // effective previous_value (a possible meter rollover). Omitted/false on
  // a first attempt so the server can hold the row back for review; the
  // operator's per-row confirm in the UI re-sends it with this set.
  confirmed_rollover?: boolean
}

interface CreateBulkReadingsInput {
  propertyId: number
  readings: BulkReadingInput[]
  read_at?: string
}

// Each row submitted to POST /readings/bulk is classified into exactly one of
// these five arrays. Replaces the legacy 422 invoice_locked short-circuit so
// users editing fresh months can save even when prior months are billed.
export interface BulkSaveResult {
  saved: Array<{ id?: number; meter_id: number }>
  locked: Array<{ meter_id: number; unit_number: string }>
  no_op: Array<{ meter_id: number }>
  cascade_blocked: Array<{
    meter_id: number
    unit_number: string
    next_reading_id: number
    reason: string
  }>
  // MON-14 (T149-028): held back, unsaved. computed_usage is
  // Reading.rollover_usage(effective_previous, current_value), the figure
  // the row would be saved with once the operator confirms it.
  rollover_needs_confirmation: Array<{ meter_id: number; computed_usage: number }>
}

export interface BulkSaveResponse {
  data: BulkSaveResult
  message: string
}

export function usePropertyReadings(propertyId: number, month?: string) {
  return useQuery({
    queryKey: ['property-readings', propertyId, month ?? null],
    queryFn: async () => {
      const params = month ? { month } : {}
      const response = await api.get<BulkReadingsResponse>(
        `/api/properties/${propertyId}/readings`,
        { params }
      )
      return response.data.data
    },
    enabled: !!propertyId,
  })
}

export function useCreateBulkReadings() {
  const queryClient = useQueryClient()

  return useMutation<BulkSaveResponse, Error, CreateBulkReadingsInput>({
    mutationFn: async ({ propertyId, readings, read_at }: CreateBulkReadingsInput) => {
      const response = await api.post<BulkSaveResponse>(
        `/api/properties/${propertyId}/readings/bulk`,
        { readings, read_at }
      )
      return response.data
    },
    onSuccess: async (_, variables) => {
      // Await refetch so callers always see fresh data after save
      await queryClient.refetchQueries({ queryKey: ['property-readings', variables.propertyId] })
    },
  })
}
